/**
 * Settings (`/settings`, `/settings/people`, `/settings/spending`,
 * `/settings/agent-runs`, `/settings/you`): one page on the same grammar as
 * Connections. One header with the sections as chips (each an address), and
 * the open section's card under it. The sections are sorted by whose they
 * are: Workspace, People and Spending are the workspace's (admins set them
 * once), Agent runs is what Wright loads and where sessions report, and You
 * is everything that follows the person: devices, tokens, skills, colour
 * scheme and the account. A page never mixes a personal control with a
 * workspace one.
 *
 * A link from anywhere in the app still opens the section it names; the
 * retired slugs (`team`, `sso`, `billing`, `tokens`) redirect to their
 * sections in `SettingsRoute`, and an address nobody knows opens Workspace.
 *
 * Everything about how a Team runs lives on that Team's page, not here.
 * Spending is gated by `view-billing` and Systems by `manage-repos`: a member
 * never sees the chip or the card, and its address opens Workspace. Systems
 * (which repositories run together) is the workspace's; a Team picks one. There is no Ask dock on this page
 * (`hasAskDock` in `shell/AppShell.tsx`): settings are forms, and the dock
 * never replaces a form.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { can, type WorkspaceMember } from "@yagni/shared";
import { useWorkspaceRole } from "../hooks/useWorkspaceRole.js";
import { ListHead } from "../queue/index.js";
import { cx } from "../queue/primitives.js";
import { SettingsView } from "../pages/SettingsView.js";
import { TeamView } from "../pages/TeamView.js";
import { BillingView } from "../pages/BillingView.js";
import { SsoSettingsView } from "../pages/settings/SsoSettingsView.js";
import { AgentRunsView } from "./AgentRunsView.js";
import { DeveloperBudgets } from "./DeveloperBudgets.js";
import { SystemsSection } from "./SystemsSection.js";
import { YouView } from "./YouView.js";

export type SettingsSectionKey = "ws" | "people" | "systems" | "spending" | "agent-runs" | "you";

export interface SettingsSection {
  key: SettingsSectionKey;
  label: string;
  /** The `/settings/<slug>` segment that opens this section; Workspace is bare `/settings`. */
  slug: string | null;
  /** The section's address. */
  path: string;
  sub: string;
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    key: "ws",
    label: "Workspace",
    slug: null,
    path: "/settings",
    sub: "Name, time, the Daily Brief, the spend ceiling, daily Bailey readings and the models serving it.",
  },
  {
    key: "people",
    label: "People",
    slug: "people",
    path: "/settings/people",
    sub: "Who is in this workspace, their roles, invites, auto-join and single sign-on.",
  },
  {
    key: "systems",
    label: "Systems",
    slug: "systems",
    path: "/settings/systems",
    sub: "Which repositories run together, for the Teams that pick one",
  },
  {
    key: "spending",
    label: "Spending",
    slug: "spending",
    path: "/settings/spending",
    sub: "Credits, invoices and per-developer budgets.",
  },
  {
    key: "agent-runs",
    label: "Agent runs",
    slug: "agent-runs",
    path: "/settings/agent-runs",
    sub: "What Wright loads when it builds, and where YAGNI Code sessions report.",
  },
  {
    key: "you",
    label: "You",
    slug: "you",
    path: "/settings/you",
    sub: "YAGNI Code on your devices, your tokens, your skills, how the app looks, and your account.",
  },
];

/**
 * The slugs Settings answered to before 2026-10-20, each to the section that
 * holds what it held. `SettingsRoute` redirects them so a saved link and the
 * CLI's device-flow URL keep landing where they meant.
 */
export const LEGACY_SETTINGS_SLUGS: Record<string, SettingsSectionKey> = {
  team: "people",
  sso: "people",
  billing: "spending",
  tokens: "you",
};

export function sectionPath(key: SettingsSectionKey): string {
  return SETTINGS_SECTIONS.find((section) => section.key === key)?.path ?? "/settings";
}

/**
 * The section an address opens; anything unknown under /settings is Workspace.
 * A retired slug never reaches here: `SettingsRoute` redirects it first.
 */
export function sectionForPath(pathname: string): SettingsSectionKey {
  const segment = pathname.replace(/^\/settings\/?/, "").split("/")[0] ?? "";
  return (
    SETTINGS_SECTIONS.find((section) => section.slug !== null && section.slug === segment)?.key ??
    "ws"
  );
}

export function cardId(key: SettingsSectionKey | "sso"): string {
  return `set-${key}`;
}

function Card({
  id,
  label,
  sub,
  children,
}: {
  id: string;
  label: string;
  sub: string;
  children: ReactNode;
}) {
  return (
    <section className="agentset" id={id} aria-labelledby={`${id}-title`}>
      <div className="ah">
        <span>
          <h2 className="nm" id={`${id}-title`}>
            {label}
          </h2>
          <div className="sub">{sub}</div>
        </span>
      </div>
      {children}
    </section>
  );
}

export function SettingsPage() {
  const { pathname } = useLocation();
  const role = useWorkspaceRole();
  // Null role is a standalone mount (tests): show everything; the backend
  // enforces the gates regardless.
  const showSpending = role === null || can(role, "view-billing");
  const showSystems = role === null || can(role, "manage-repos");
  const sections = SETTINGS_SECTIONS.filter(
    (section) =>
      (section.key !== "spending" || showSpending) && (section.key !== "systems" || showSystems),
  );
  const target = sectionForPath(pathname);
  const current: SettingsSectionKey =
    (target === "spending" && !showSpending) || (target === "systems" && !showSystems)
      ? "ws"
      : target;
  const [members, setMembers] = useState<WorkspaceMember[] | null>(null);
  const owner = members?.find((member) => member.role === "owner")?.displayName;

  // The owner is a header fact on every section. On People the Members
  // card's own read feeds it; elsewhere the page reads the members once itself.
  useEffect(() => {
    if (current === "people" || members !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/members", { credentials: "include" });
        if (!res.ok) {
          console.warn(
            `Settings: the members read failed (${res.status}); the owner fact stays off the header.`,
          );
          return;
        }
        const data = (await res.json()) as { members?: WorkspaceMember[] };
        if (!cancelled && Array.isArray(data.members)) setMembers(data.members);
      } catch (error) {
        console.warn(
          "Settings: the members read failed; the owner fact stays off the header.",
          error,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [current, members]);

  const section = sections.find((entry) => entry.key === current) ?? sections[0]!;

  const chips = sections.map((entry) => (
    <Link
      key={entry.key}
      to={entry.path}
      className={cx("chip tab", entry.key === current && "on")}
      aria-current={entry.key === current ? "page" : undefined}
    >
      {entry.label}
    </Link>
  ));

  return (
    <div className="wsurf settings" data-testid="settings-surface">
      <ListHead
        title="Settings"
        sub={[
          "Everything about how a Team runs lives on that Team. This is the rest.",
          ...(owner ? [`Owner: ${owner}`] : []),
        ]}
        acts={
          <Link to="/services" className="btn quiet sm">
            Connections →
          </Link>
        }
        chips={
          <nav className="tabs" aria-label="Settings sections">
            {chips}
          </nav>
        }
      />
      <div className="qlist">
        {current === "ws" ? (
          <Card id={cardId("ws")} label={section.label} sub={section.sub}>
            <SettingsView />
          </Card>
        ) : current === "people" ? (
          <>
            <Card id={cardId("people")} label={section.label} sub={section.sub}>
              <TeamView onMembers={setMembers} />
            </Card>
            <Card
              id={cardId("sso")}
              label="Single sign-on"
              sub="SAML through your identity provider."
            >
              <div className="body">
                <SsoSettingsView />
              </div>
            </Card>
          </>
        ) : current === "systems" ? (
          <Card id={cardId("systems")} label={section.label} sub={section.sub}>
            <SystemsSection />
          </Card>
        ) : current === "spending" ? (
          <Card id={cardId("spending")} label={section.label} sub={section.sub}>
            <div className="body">
              <BillingView />
              <DeveloperBudgets />
            </div>
          </Card>
        ) : current === "agent-runs" ? (
          <Card id={cardId("agent-runs")} label={section.label} sub={section.sub}>
            <AgentRunsView />
          </Card>
        ) : (
          <Card id={cardId("you")} label={section.label} sub={section.sub}>
            <YouView />
          </Card>
        )}
      </div>
    </div>
  );
}
